
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, Phone } from "lucide-react";

interface SiteDepartmentSEOProps {
  deptName: string;
  deptCode: string;
  seoContent: {
    title: string;
    services: string[];
    zones: string[];
  };
}

const SiteDepartmentSEO: React.FC<SiteDepartmentSEOProps> = ({ 
  deptName, 
  deptCode,
  seoContent 
}) => {
  return (
    <div className="mb-12 bg-white rounded-lg shadow-md p-8">
      <h2 className="text-2xl font-heading font-bold mb-4 text-fabre-gray">
        Solutions de gestion des déchets dans le département {deptName}
      </h2>
      
      <div className="prose max-w-none text-gray-700">
        <p className="mb-4">
          Groupe Fabre Environnement propose des services complets de gestion, collecte et valorisation des déchets dans tout le département {deptName} ({deptCode}). 
          Notre engagement pour l'environnement et notre expertise nous permettent d'offrir des solutions sur mesure pour les entreprises, collectivités et particuliers 
          {seoContent.zones.length > 0 ? ` de ${seoContent.zones.slice(0, 3).join(', ')} et l'ensemble du territoire.` : '.'}
        </p>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 text-fabre-darkgreen">
          Nos services disponibles dans {deptName}
        </h3>
        
        <ul className="list-disc pl-6 mb-6">
          {seoContent.services.map((service, index) => (
            <li key={index} className="mb-2">
              <strong>{service}</strong> : {index === 0 
                ? "Solutions adaptées à tous vos chantiers et besoins spécifiques avec des capacités variées." 
                : index === 1 
                  ? "Service régulier ou ponctuel pour les entreprises avec traçabilité complète."
                  : index === 2
                    ? "Transformation de vos déchets en ressources avec un taux de valorisation optimal."
                    : index === 3
                      ? "Rachat de tous types de métaux aux meilleurs prix du marché."
                      : "Interventions professionnelles respectueuses des normes environnementales."
              }
            </li>
          ))}
        </ul>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 text-fabre-darkgreen">
          Zones d'intervention dans {deptName}
        </h3>
        
        <p className="mb-4">
          Nous intervenons dans l'ensemble du département {deptCode}, notamment 
          {seoContent.zones.length > 0 
            ? ` à ${seoContent.zones.join(', ')} et leurs environs.` 
            : " dans toutes les communes du territoire."}
          Notre flotte de véhicules moderne nous permet de répondre rapidement à vos besoins partout dans {deptName}.
        </p>
        
        <h3 className="text-xl font-semibold mt-6 mb-3 text-fabre-darkgreen">
          Pourquoi choisir Groupe Fabre Environnement dans {deptName} ?
        </h3>
        
        <ul className="list-disc pl-6 mb-6">
          <li className="mb-2"><strong>Expertise locale</strong> : Connaissance approfondie des enjeux spécifiques au territoire</li>
          <li className="mb-2"><strong>Engagement environnemental</strong> : 85% de taux moyen de valorisation des déchets collectés</li>
          <li className="mb-2"><strong>Solutions personnalisées</strong> : Adaptées aux besoins de chaque client</li>
          <li className="mb-2"><strong>Réactivité</strong> : Intervention rapide sur l'ensemble du département</li>
          <li className="mb-2"><strong>Traçabilité complète</strong> : Suivi rigoureux de vos déchets</li>
        </ul>
        
        <div className="bg-fabre-lightgreen p-6 rounded-lg mt-8 text-center">
          <h3 className="text-xl font-semibold mb-3 text-fabre-darkgreen">
            Besoin de nos services dans {deptName} ?
          </h3>
          <p className="mb-4">
            Notre équipe est à votre disposition pour étudier vos besoins et vous proposer la solution la plus adaptée. 
            Contactez-nous dès aujourd'hui pour un devis personnalisé ou plus d'informations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-4">
            <Button asChild className="bg-fabre-green hover:bg-fabre-darkgreen flex items-center">
              <Link to="/contact">
                <Mail className="mr-2 h-5 w-5" />
                Nous contacter
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-fabre-green text-fabre-green hover:bg-fabre-lightgreen flex items-center">
              <Link to="/nos-services/location-bennes" className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                Location de bennes
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteDepartmentSEO;
